import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { envConfig } from '@bg-shared/constants';
import { chatStoreSupabase } from './Chat.store';
import { ISupabaseAuthResponse, IDatabase, IProfile } from './interfaces';
import { ChatUtilities } from './Chat.utilities';

class SupabaseService {
    private client: SupabaseClient<IDatabase> | null = null;

    constructor() {
        if (this.client || !envConfig.chatConfig?.supabase) {
            return;
        }
        this.setup(envConfig.chatConfig.supabase.url, envConfig.chatConfig.supabase.key);
    }

    private setup(supabaseUrl: string, supabaseKey: string): SupabaseClient<IDatabase> {
        if (!this.client) {
            this.client = createClient<IDatabase>(supabaseUrl, supabaseKey, {
                realtime: {
                    params: {
                        eventsPerSecond: 10,
                    },
                },
            });
        }
        return this.client;
    }

    public getClient(): SupabaseClient<IDatabase> {
        if (!this.client) {
            throw new Error('Supabase client not initialized. Call setup() first.');
        }
        return this.client;
    }

    public async authenticate(externalAuth: ISupabaseAuthResponse): Promise<void> {
        if (chatStoreSupabase.value.isLoggedIn) {
            return;
        }

        const { accessToken, refreshToken } = externalAuth;

        const client = this.getClient();

        try {
            const { data, error } = await client.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
            });

            if (error || !data.user) {
                ChatUtilities.handleError(error || 'chat_session_error');
                return;
            }

            const { data: existingProfile } = await client
                .from('profiles')
                .select('*')
                .eq('user_id', data.user.id)
                .single();

            if (!existingProfile) {
                ChatUtilities.handleError('chat_profile_error');
                return;
            }

            chatStoreSupabase.setAuth(existingProfile);
        } catch (error) {
            ChatUtilities.handleError(error);
        }
    }

    public async getProfile(profileId: string): Promise<IProfile> {
        const cachedProfile = chatStoreSupabase.getCachedProfileById(profileId);

        if (cachedProfile) return cachedProfile;

        const { data, error } = await this.getClient()
            .from('profiles')
            .select('*')
            .eq('id', profileId)
            .single();

        if (error) {
            ChatUtilities.handleError(error);
            return null;
        }

        chatStoreSupabase.updateProfile(data);

        return data;
    }

    public createChannel(channelName: string) {
        return this.getClient().channel(channelName);
    }
}

export const supabaseService = new SupabaseService();
